import { InputWithLabel, SelectWithLabel } from '@adotel/components'
import { DateObject } from '@adotel/components/build/helpers/date'
import { useLazyQuery } from '@apollo/client'
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Checkbox,
  Typography,
  TextField
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'

import { DatePickerInputWithLabel } from '../../../../components/DatePickerInputWithLabel'
import { ITTIC_REERVATION_TYPES } from '../../../../graphql/tenant'
import { combineDateTime, splitDateTime } from '../../../../helpers/date'
import {
  allowedEmailRegex,
  elevenNumbersOnlyRegex,
  englishRegex,
  iranMobileRegex,
  persianLetterRegex,
  persianRegex,
  strictEmailFormatRegex,
  tenNumbersOnlyRegex
} from '../../../../helpers/regex'
import { getRoleFromStorage } from '../../../../helpers/userData'
import { getTranslate, lang } from '../../../../localization'
import { ROLE } from '../../../../staticRoleManagement/types'
import {
  ConfirmPrimitiveReservationInput,
  PMSRequestedMethod
} from '../../../../types/cart'
import { ItticReservationType } from '../../../../types/tenants'
import { canChoosePmsRequestedMethod } from '../../pmsRequestedMethod'
import { validateIdentificationNo } from '../helpers/helper'
import { useStyles } from './styles/index.styles'

type BookerDetailsFormProps = {
  confirmPrimitiveInput: ConfirmPrimitiveReservationInput
  setConfirmPrimitiveInput: React.Dispatch<
    React.SetStateAction<ConfirmPrimitiveReservationInput>
  >

  setOptionReservationExpiry: React.Dispatch<React.SetStateAction<boolean>>
  optionReservationExpiry: boolean
  setFillBookerDetalisEnabled: React.Dispatch<React.SetStateAction<boolean>>
  showDiscount: boolean
  showOtionReserveExpiryTime: boolean
  showReservationType: boolean
  optionReserveMaxExpireTime: string
}

const BookerDetailsForm = ({
  confirmPrimitiveInput,
  setConfirmPrimitiveInput,
  setOptionReservationExpiry,
  optionReservationExpiry,
  setFillBookerDetalisEnabled,
  showDiscount,
  showOtionReserveExpiryTime,
  showReservationType,
  optionReserveMaxExpireTime
}: BookerDetailsFormProps) => {
  const classes = useStyles()
  const [dateObject, setdateObject] = useState<DateObject>({})
  const [time, setTime] = useState('10:00')

  useEffect(() => {
    if (dateObject.gregorianStartDate && time) {
      const expiry = combineDateTime(dateObject.gregorianStartDate, time)
      setConfirmPrimitiveInput({
        ...confirmPrimitiveInput,
        expiresAt: expiry
      })
    }
  }, [dateObject, time])

  //ITTIC_RESERVATION_TYPES_QUERY
  const [getItticReservationTypes, { data: itticReservationTypes }] =
    useLazyQuery<{
      getItticReservationTypes: ItticReservationType[]
    }>(ITTIC_REERVATION_TYPES, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only'
    })

  useEffect(() => {
    if (showReservationType) {
      getItticReservationTypes({ variables: { tenantCode: 102 } })
    }
  }, [showReservationType])

  const isPersianName = persianRegex.test(
    confirmPrimitiveInput.booker.firstName
  )
  const isPersianLastName = persianRegex.test(
    confirmPrimitiveInput.booker.lastName
  )

  const isRoleAccOperator = getRoleFromStorage() === ROLE.ACCOMMODATION_OPERATOR

  const maxExpiry = splitDateTime(optionReserveMaxExpireTime)

  const onChangeTime = (
    selectedTime: string,
    maxExpiryDate: string,
    maxExpiryTime: string,
    selectedDate: string
  ) => {
    if (selectedDate === maxExpiryDate) {
      if (selectedTime > maxExpiryTime) {
        const alertMessage =
          lang === 'fa'
            ? `زمان انتخاب شده از زمان مجاز ${maxExpiryTime} بیشتر است. زمان به ${maxExpiryTime} تنظیم شد.`
            : `The selected time exceeds the allowed maximum time of ${maxExpiryTime}. Setting time to ${maxExpiryTime}.`

        // Show the alert
        window.alert(alertMessage)
        setTime(maxExpiryTime)
      } else {
        setTime(selectedTime)
      }
    } else if (selectedDate < maxExpiryDate) {
      setTime(selectedTime)
    }
  }

  return (
    <div className={classes.formContainer}>
      <Typography variant="h4" className={classes.header}>
        {getTranslate('The Booker Information')}
      </Typography>
      <RadioGroup
        row
        value={confirmPrimitiveInput.booker.isForeigner ? 'Foreign' : 'Iranian'}
        onChange={(e) => {
          const isForeign = e.target.value === 'Foreign'
          setConfirmPrimitiveInput({
            ...confirmPrimitiveInput,
            booker: { ...confirmPrimitiveInput.booker, isForeigner: isForeign }
          })
        }}>
        <FormControlLabel
          value="Iranian"
          control={
            <Radio
              color="primary"
              size="small"
              classes={{
                root: classes.radioIcon
              }}
            />
          }
          label={getTranslate('Iranian Guest')}
          classes={{ label: classes.label }}
        />
        <FormControlLabel
          value="Foreign"
          control={
            <Radio
              color="primary"
              size="small"
              classes={{
                root: classes.radioIcon
              }}
            />
          }
          label={getTranslate('Foreign Guest')}
          classes={{ label: classes.label }}
        />
      </RadioGroup>
      <div className={classes.row}>
        <InputWithLabel
          name="firstName"
          autoComplete="given-name"
          label={getTranslate('First Name')}
          className={classes.input}
          style={isPersianName ? { fontFamily: 'IranSans' } : undefined}
          value={confirmPrimitiveInput.booker.firstName}
          onChange={(e) => {
            const value = e.currentTarget.value
            setConfirmPrimitiveInput({
              ...confirmPrimitiveInput,
              booker: {
                ...confirmPrimitiveInput.booker,
                firstName: value
              }
            })
          }}
          helpertext={
            !(confirmPrimitiveInput.booker.isForeigner
              ? englishRegex.test(confirmPrimitiveInput.booker.firstName)
              : persianLetterRegex.test(
                  confirmPrimitiveInput.booker.firstName
                )) && confirmPrimitiveInput.booker.firstName.length > 0
              ? confirmPrimitiveInput.booker.isForeigner
                ? getTranslate(
                    'Only English letters are allowed for foreign guests'
                  )
                : getTranslate('Just Enter Persian Letters')
              : ''
          }
          error={
            !(confirmPrimitiveInput.booker.isForeigner
              ? englishRegex.test(confirmPrimitiveInput.booker.firstName)
              : persianLetterRegex.test(
                  confirmPrimitiveInput.booker.firstName
                )) && confirmPrimitiveInput.booker.firstName.length > 0
          }
        />
        <InputWithLabel
          name="lastName"
          autoComplete="family-name"
          label={getTranslate('Last Name')}
          className={classes.input}
          style={isPersianLastName ? { fontFamily: 'IranSans' } : undefined}
          value={confirmPrimitiveInput.booker.lastName}
          onChange={(e) => {
            const value = e.currentTarget.value
            setConfirmPrimitiveInput({
              ...confirmPrimitiveInput,
              booker: {
                ...confirmPrimitiveInput.booker,
                lastName: value
              }
            })
          }}
          helpertext={
            !(confirmPrimitiveInput.booker.isForeigner
              ? englishRegex.test(confirmPrimitiveInput.booker.lastName)
              : persianLetterRegex.test(
                  confirmPrimitiveInput.booker.lastName
                )) && confirmPrimitiveInput.booker.lastName.length > 0
              ? confirmPrimitiveInput.booker.isForeigner
                ? getTranslate(
                    'Only English letters are allowed for foreign guests'
                  )
                : getTranslate('Just Enter Persian Letters')
              : ''
          }
          error={
            !(confirmPrimitiveInput.booker.isForeigner
              ? englishRegex.test(confirmPrimitiveInput.booker.lastName)
              : persianLetterRegex.test(
                  confirmPrimitiveInput.booker.lastName
                )) && confirmPrimitiveInput.booker.lastName.length > 0
          }
        />
        <InputWithLabel
          name="email"
          autoComplete="email"
          label={getTranslate('Email')}
          className={classes.input}
          type="email"
          value={confirmPrimitiveInput.booker.email}
          error={
            !strictEmailFormatRegex.test(confirmPrimitiveInput.booker.email) &&
            confirmPrimitiveInput.booker.email.length > 0
          }
          helpertext={
            !strictEmailFormatRegex.test(confirmPrimitiveInput.booker.email) &&
            confirmPrimitiveInput.booker.email.length > 0
              ? getTranslate(
                  'Email address must contain only English letters, numbers, and have a valid format.'
                )
              : ''
          }
          onChange={(e) => {
            const value = e.currentTarget.value
            if (!allowedEmailRegex.test(value)) return
            setConfirmPrimitiveInput({
              ...confirmPrimitiveInput,
              booker: { ...confirmPrimitiveInput.booker, email: value }
            })
          }}
        />
      </div>
      <div className={classes.row}>
        <InputWithLabel
          name="phone"
          autoComplete="tel"
          label={getTranslate('Mobile Number')}
          type="number"
          value={confirmPrimitiveInput.booker.mobile}
          min={0}
          onKeyDown={(e) => {
            if (e.key === '-' || e.key === 'e' || e.key === '.') {
              e.preventDefault()
            }
          }}
          error={
            confirmPrimitiveInput.booker.mobile.length > 0 &&
            !iranMobileRegex.test(confirmPrimitiveInput.booker.mobile)
          }
          helpertext={
            confirmPrimitiveInput.booker.mobile.length > 0 &&
            !iranMobileRegex.test(confirmPrimitiveInput.booker.mobile)
              ? getTranslate('Please enter a valid phone numbers')
              : ''
          }
          onChange={(e) => {
            const rawValue = e.currentTarget.value

            if (elevenNumbersOnlyRegex.test(rawValue)) {
              setConfirmPrimitiveInput({
                ...confirmPrimitiveInput,
                booker: {
                  ...confirmPrimitiveInput.booker,
                  mobile: rawValue
                }
              })
            }
          }}
          placeholder={getTranslate('Type only numbers')}
          className={[classes.input, classes.inputNumber].join(' ')}
        />
        <InputWithLabel
          name="national-id"
          value={confirmPrimitiveInput.booker.identificationNo}
          label={getTranslate(
            `${
              confirmPrimitiveInput.booker.isForeigner
                ? 'Passport Number'
                : 'ID Card Number'
            }`
          )}
          error={
            confirmPrimitiveInput.booker.identificationNo.length > 0 &&
            !validateIdentificationNo(
              confirmPrimitiveInput.booker.identificationNo.slice(0, 10),
              confirmPrimitiveInput.booker.isForeigner
            )
          }
          helpertext={
            confirmPrimitiveInput.booker.identificationNo.length > 0 &&
            !validateIdentificationNo(
              confirmPrimitiveInput.booker.identificationNo.slice(0, 10),
              confirmPrimitiveInput.booker.isForeigner
            )
              ? confirmPrimitiveInput.booker.isForeigner
                ? getTranslate(
                    'Please enter a valid passport number (6–9 alphanumeric).'
                  )
                : getTranslate('The national ID must be 10 digits and valid.')
              : ''
          }
          type="text"
          min={0}
          placeholder={getTranslate('Type only numbers')}
          onKeyDown={(e) => {
            if (
              e.key === '-' ||
              e.key === 'e' ||
              e.key === '+' ||
              e.key === '.'
            ) {
              e.preventDefault()
            }
          }}
          className={[classes.input, classes.inputNumber].join(' ')}
          onChange={(event) => {
            const rawInput = event.currentTarget.value
            const inputValue = rawInput.replace(/\s+/g, '')

            if (
              tenNumbersOnlyRegex.test(inputValue) ||
              confirmPrimitiveInput.booker.isForeigner
            ) {
              setConfirmPrimitiveInput({
                ...confirmPrimitiveInput,
                booker: {
                  ...confirmPrimitiveInput.booker,
                  identificationNo: inputValue
                }
              })
            }
          }}
        />
        {showReservationType && (
          <SelectWithLabel
            label={getTranslate('Reservation Type')}
            placeholder={getTranslate('Choose reservation type')}
            size="small"
            className={classes.input}
            // disabled={isRoleAccOperator}
            onChange={(e) =>
              setConfirmPrimitiveInput({
                ...confirmPrimitiveInput,
                itticReservationType: {
                  ID:
                    itticReservationTypes?.getItticReservationTypes.find(
                      (type) => type.code === Number(e.target.value)
                    )?.ID || '',
                  code: Number(e.target.value),
                  name: itticReservationTypes?.getItticReservationTypes.find(
                    (type) => type.code === Number(e.target.value)
                  )?.name || { en: '', fa: '' }
                }
              })
            }>
            {itticReservationTypes?.getItticReservationTypes
              .filter((ele) => {
                if (isRoleAccOperator) {
                  return ele.code === 2
                } else {
                  return true
                }
              })
              .map((item, index) => (
                <MenuItem key={index} value={item.code}>
                  {lang === 'fa' ? item.name.fa : item.name.en}
                </MenuItem>
              ))}
          </SelectWithLabel>
        )}
      </div>
      <div className={classes.row}>
        {showDiscount && (
          <InputWithLabel
            label={getTranslate('Discount')}
            className={classes.input}
            type="number"
            min={0}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'e' || e.key === '+') {
                e.preventDefault()
              }
            }}
            placeholder={getTranslate('Type only numbers')}
            onChange={(e) => {
              const value = e.currentTarget.value
              setConfirmPrimitiveInput({
                ...confirmPrimitiveInput,
                discountPercent: value === '' ? 0 : Number(value)
              })
            }}
          />
        )}
      </div>
      {showOtionReserveExpiryTime && (
        <div className={classes.row}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                size="small"
                onChange={(e) => setOptionReservationExpiry(e.target.checked)}
              />
            }
            label={getTranslate('Option reservation max expiry date time')}
            classes={{ label: classes.label }}
          />
          <DatePickerInputWithLabel
            label={getTranslate('Expiry Date')}
            autoComplete="off"
            inputSize="small"
            disabled={!optionReservationExpiry}
            datePickerProps={{
              onChange: (d: DateObject) => setdateObject(d),
              range: false,
              dateObject: dateObject,
              numberOfVisibleMonths: 1,
              disableAfter: maxExpiry.date
            }}
          />
          <div>
            <Typography variant="h5" className={classes.timeText}>
              {getTranslate('Expiration time')}
            </Typography>
            <TextField
              id="time"
              type="time"
              variant="outlined"
              onChange={(e) =>
                onChangeTime(
                  e.target.value,
                  maxExpiry.date,
                  maxExpiry.time,
                  dateObject.gregorianStartDate || ''
                )
              }
              className={[
                classes.textField,
                !optionReservationExpiry ? classes.inputDisable : ''
              ].join(' ')}
              classes={{ root: classes.textRoot }}
              InputLabelProps={{
                shrink: true
              }}
              inputProps={{
                step: 60, // 60s = 1min
                value: time
              }}
            />
          </div>
        </div>
      )}
      <div className={classes.row}>
        {canChoosePmsRequestedMethod(getRoleFromStorage()) && (
          <div>
            <Typography variant="h5" className={classes.text}>
              {getTranslate('PMS Request Method')}
            </Typography>
            <RadioGroup
              row
              value={confirmPrimitiveInput.pmsRequestedMethod}
              onChange={(e) => {
                setConfirmPrimitiveInput({
                  ...confirmPrimitiveInput,
                  pmsRequestedMethod: e.target.value as PMSRequestedMethod
                })
              }}>
              <FormControlLabel
                value="Sync"
                control={
                  <Radio
                    color="primary"
                    size="small"
                    classes={{
                      root: classes.radioIcon
                    }}
                  />
                }
                label={getTranslate('Sync')}
                classes={{ label: classes.label }}
              />
              <FormControlLabel
                value="Async"
                control={
                  <Radio
                    color="primary"
                    size="small"
                    classes={{
                      root: classes.radioIcon
                    }}
                  />
                }
                label={getTranslate('Async')}
                classes={{ label: classes.label }}
              />
              <FormControlLabel
                value="Auto"
                control={
                  <Radio
                    color="primary"
                    size="small"
                    classes={{
                      root: classes.radioIcon
                    }}
                  />
                }
                label={getTranslate('Auto')}
                classes={{ label: classes.label }}
              />
            </RadioGroup>
          </div>
        )}
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              size="small"
              onChange={(e) => setFillBookerDetalisEnabled(e.target.checked)}
            />
          }
          label={getTranslate('Fill booker detail as guest detail')}
          classes={{ label: classes.label }}
        />
      </div>
    </div>
  )
}

export default BookerDetailsForm
